import { useCallback, useEffect, useState } from "react";
import type { CfaoVehicle } from "@/data/cfaoVehicles";
import { getVehicleById, getVehicleBySlug, getVehicles } from "@/services/vehicles";

type State<T> = { data: T; loading: boolean; error: string };

export function useVehicles() {
  const [state, setState] = useState<State<CfaoVehicle[]>>({ data: [], loading: true, error: "" });
  const refresh = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: "" }));
    try {
      setState({ data: await getVehicles(), loading: false, error: "" });
    } catch (error) {
      setState({ data: [], loading: false, error: error instanceof Error ? error.message : "Unable to load vehicles." });
    }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);
  return { vehicles: state.data, loading: state.loading, error: state.error, refresh };
}

export function useVehicle(identifier: string | undefined, by: "slug" | "id" = "slug") {
  const [state, setState] = useState<State<CfaoVehicle | null>>({ data: null, loading: true, error: "" });
  useEffect(() => {
    let active = true;
    if (!identifier) {
      setState({ data: null, loading: false, error: "" });
      return () => { active = false; };
    }
    setState((current) => ({ ...current, loading: true, error: "" }));
    const request = by === "id" ? getVehicleById(identifier) : getVehicleBySlug(identifier);
    request.then((data) => {
      if (active) setState({ data, loading: false, error: "" });
    }).catch((error) => {
      if (active) setState({ data: null, loading: false, error: error instanceof Error ? error.message : "Unable to load vehicle." });
    });
    return () => { active = false; };
  }, [identifier, by]);
  return { vehicle: state.data, loading: state.loading, error: state.error };
}
